import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import styled from "@emotion/styled";
import { debounce } from "lodash";
import { smoothTransition } from "styles/animations";

import { CustomerDocumentDrawer } from "components/customer_documents/CustomerDocumentDrawer";
import Collapsible from "components/ui/collapsible/Collapsible";
import toast from "components/ui/toast/Toast";
import { useSubscription } from "lib/hooks/subscription";
import { usePaywall } from "lib/providers/Paywall";
import { type BidResponseDto } from "../../../lib/hooks/api/bid/bid_responses/useBidResponse";
import { useUpdateBidResponses } from "../../../lib/hooks/api/bid/bid_responses/useUpdateBidResponses";
import { useUnsavedChangeWarning } from "../../../lib/hooks/useUnsavedChangesWarning";
import { formatDate } from "../../../lib/utils";
import { Flex, Text } from "../../../styles/utility-components";
import { BlockNoteEditor, BlockNoteEditorRef } from "../../ui/blocknote_editor/BlockNoteEditor";
import ExportModal, { ExportType } from "../../ui/blocknote_editor/ExportModal";
import { StotlesPartialBlock } from "../../ui/blocknote_editor/types";
import {
  ExportHeaderContent,
  isBlockNoteEmpty,
  validateInitialContent,
} from "../../ui/blocknote_editor/utils";
import { BreadcrumbContainer, RouterBreadcrumb } from "../../ui/breadcrumb/SimpleBreadcrumb";
import Button from "../../ui/button/Button";
import { DropdownMenu, DropdownMenuItem, DropdownMenuTrigger } from "../../ui/dropdown_menu";
import EditableMarkdownText from "../../ui/editable_markdown_text/EditableMarkdownText";
import EditableText from "../../ui/editable_text/EditableText";
import Tabs, { TabItem } from "../../ui/tabs/Tabs";
import AiAssistant from "../bid_writing_question_section/AiAssistant";
import { AnswerSource } from "../types";
import BidResponseDetailsSidebar from "./bid_response_details_sidebar/BidResponseDetailsSidebar";
import { stripCitationBrackets } from "./utils";

interface BidResponseContentProps {
  bidResponse: BidResponseDto;
}

const TabKeys = {
  AiAssistant: "ai-assistant",
  Details: "details",
};

export function BidResponseContent({ bidResponse }: BidResponseContentProps) {
  const editorRef = useRef<BlockNoteEditorRef>(null);
  const [, setIsEditorEmpty] = useState(true);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [exportModal, setExportModal] = useState<{ open: boolean; type: ExportType }>({
    open: false,
    type: "pdf",
  });
  const [isAutoSaving, setIsAutoSaving] = useState(false);
  const [lastSaved, setLastSaved] = useState<Date | null>(null);

  const [isSidebarOpen, setIsSidebarOpen] = useState(true);
  const toggleSidebar = () => setIsSidebarOpen((prev) => !prev);

  const [sidebarWidth, setSidebarWidth] = useState(500);
  const isResizing = useRef(false);

  const [selectedSource, setSelectedSource] = useState<AnswerSource | undefined>();

  const { openPaywallModal } = usePaywall();
  const subscription = useSubscription();

  const hasBidAccess = subscription.hasDataTypes("BIDS");

  const onMouseMove = (e: MouseEvent) => {
    if (!isResizing.current) return;
    const newWidth = window.innerWidth - e.clientX;
    // Clamp the width between 350px and 60% of viewport width
    const maxWidth = window.innerWidth * 0.6;
    const clamped = Math.min(Math.max(newWidth, 350), maxWidth);
    setSidebarWidth(clamped);
  };

  const onMouseUp = () => {
    isResizing.current = false;
    window.removeEventListener("mousemove", onMouseMove);
    window.removeEventListener("mouseup", onMouseUp);
  };

  const onMouseDown = () => {
    isResizing.current = true;
    window.addEventListener("mousemove", onMouseMove);
    window.addEventListener("mouseup", onMouseUp);
  };

  // Update bid response functionality
  const updateBidResponse = useUpdateBidResponses();
  const isSaving = updateBidResponse.isPending;
  const canSave = Boolean(bidResponse.id);

  // Track unsaved changes
  const handleContentChange = (content: StotlesPartialBlock[]) => {
    setIsEditorEmpty(isBlockNoteEmpty(content));
    setHasUnsavedChanges(true);
  };

  const saveContent = useCallback(
    async ({ showToast = false } = {}) => {
      if (!canSave || !bidResponse.id || !editorRef.current) return;

      const content = editorRef.current.getContent();
      const input = {
        ids: [bidResponse.id],
        response: JSON.stringify(content),
      };

      if (showToast) {
        toast.promise(updateBidResponse.mutateAsync({ input }), {
          loading: "Saving...",
          success: () => {
            setHasUnsavedChanges(false);
            return "Saved successfully!";
          },
          error: "Failed to save",
        });
      } else {
        try {
          setIsAutoSaving(true);
          await updateBidResponse.mutateAsync({ input });
          setHasUnsavedChanges(false);
          setLastSaved(new Date());
        } catch {
          toast.error("Failed to save");
        } finally {
          setIsAutoSaving(false);
        }
      }
    },
    [canSave, bidResponse.id, updateBidResponse],
  );

  const handleSave = () => saveContent({ showToast: true });

  const debouncedSave = useMemo(
    () => debounce(() => saveContent({ showToast: false }), 3000),
    [saveContent],
  );

  useEffect(() => {
    if (hasUnsavedChanges && canSave) {
      void debouncedSave();
    }
    return () => debouncedSave.cancel();
  }, [hasUnsavedChanges, canSave, debouncedSave]);

  const handleTitleUpdate = (newTitle: string) => {
    if (bidResponse.id) {
      const input = {
        ids: [bidResponse.id],
        title: newTitle,
      };

      toast.promise(updateBidResponse.mutateAsync({ input }), {
        loading: "Updating title...",
        success: "Title updated successfully!",
        error: "Failed to update title",
      });
    }
  };

  const handleDescriptionUpdate = (newDescription: string) => {
    if (bidResponse.id) {
      const input = {
        ids: [bidResponse.id],
        description: newDescription,
      };

      toast.promise(updateBidResponse.mutateAsync({ input }), {
        loading: "Updating description...",
        success: "Description updated successfully!",
        error: "Failed to update description",
      });
    }
  };

  useUnsavedChangeWarning(hasUnsavedChanges, { enablePopstateWarning: true });

  const uploadFile = async (file: File): Promise<string> => {
    // TODO: Implement the file upload here when the backend is ready
    // Essentially, this should work similar to the bid docs upload. It should
    // create a signed url and upload the file to that url
    const blob = new Blob([file], { type: file.type });
    return URL.createObjectURL(blob);
  };

  const handleExportDocx = () => {
    setExportModal({ open: true, type: "docx" });
  };

  const handleExportPdf = () => {
    setExportModal({ open: true, type: "pdf" });
  };

  const handleExport = async (filename: string, exportType: ExportType) => {
    const headerContent: ExportHeaderContent | undefined =
      bidResponse.title && bidResponse.description
        ? {
            title: bidResponse.title,
            description: bidResponse.description,
          }
        : undefined;

    if (exportType === "pdf") {
      await editorRef.current?.exportToPdf(filename, headerContent);
    } else {
      await editorRef.current?.exportToDocx(filename, headerContent);
    }
  };

  const tabItems: TabItem[] = [
    {
      key: TabKeys.AiAssistant,
      label: "AI assistant",
      icon: "magicWand",
      showPaywall: !hasBidAccess,
      children: (
        <AiAssistant
          resourceId={bidResponse.id}
          resourceName={bidResponse.title}
          bidQuestion={bidResponse.description || ""}
          onInsertAnswer={(answer) => {
            editorRef.current?.insertContent(
              stripCitationBrackets(answer),
              editorRef.current?.isFocused() ? "cursor" : "end",
            );
          }}
        />
      ),
    },
    {
      key: TabKeys.Details,
      label: "Details",
      icon: "infoCircle",
      children: (
        <BidResponseDetailsSidebar
          bidResponse={bidResponse}
          onSourceSelect={(source) =>
            source && setSelectedSource({ ...source, fileName: source.title })
          }
        />
      ),
    },
  ];

  const SaveStatus = () => {
    if (isAutoSaving) {
      return <Text color="sysTextSecondary">Auto-saving...</Text>;
    }

    if (hasUnsavedChanges) {
      return <Text color="sysTextSecondary">Unsaved changes</Text>;
    }

    if (lastSaved) {
      return (
        <Text color="sysTextSecondary"> Last saved {formatDate(lastSaved, "en-GB", "HH:mm")}</Text>
      );
    }

    return null;
  };
  const bidId = bidResponse.bid?.id ?? undefined;
  const bidName = bidResponse.bid?.name ?? "Bid";
  const responseTitle = bidResponse.title || "Untitled response";

  const breadcrumbOverrides: Record<string, { label: string }> = bidId
    ? {
        [`/bids/${bidId}`]: { label: bidName },
        [`/bids/${bidId}/bid_responses/${bidResponse.id}`]: { label: responseTitle },
      }
    : {
        [`/bid_responses/${bidResponse.id}`]: { label: responseTitle },
      };

  return (
    <Container>
      <BreadcrumbContainer>
        <RouterBreadcrumb overrides={breadcrumbOverrides} />
        <Flex gap={12} alignItems="center">
          <SaveStatus />
          {hasUnsavedChanges && (
            <Button
              icon="save01"
              buttonStyle="inverse"
              onClick={handleSave}
              disabled={!canSave || isSaving}
              aria-label="Save bid response"
            >
              Save
            </Button>
          )}
          <DropdownMenu
            trigger={<DropdownMenuTrigger icon="download01">Export</DropdownMenuTrigger>}
          >
            <DropdownMenuItem onClick={handleExportDocx}>Export as Word (.docx)</DropdownMenuItem>
            <DropdownMenuItem onClick={handleExportPdf}>Export as PDF</DropdownMenuItem>
          </DropdownMenu>
          <Button
            icon="layoutRight"
            buttonType="primary"
            buttonStyle="inverse"
            onClick={toggleSidebar}
          />
        </Flex>
      </BreadcrumbContainer>

      <Flex flexGrow={1} style={{ overflow: "hidden" }}>
        <Content>
          <Flex justifyContent={isSidebarOpen ? "space-between" : "center"} height="100%">
            <MainContent sidebarWidth={sidebarWidth}>
              <Collapsible
                title={
                  <EditableText
                    textProps={{
                      children: bidResponse.title || "Untitled response",
                      h3: true,
                      bold: true,
                    }}
                    placeholder="Enter a title"
                    emptyText="Untitled response"
                    onSave={handleTitleUpdate}
                    autoFocus={false}
                    padding="8px"
                  />
                }
                defaultOpen
                isBordered
                contentWrapperStyles={{ paddingTop: 24 }}
              >
                <EditableMarkdownText
                  initialValue={bidResponse.description || ""}
                  hasEditAccess
                  placeholder="Enter a description"
                  onSave={handleDescriptionUpdate}
                  padding="8px"
                />
              </Collapsible>
              <BlockNoteEditor
                ref={editorRef}
                theme="light"
                minHeight="0"
                sideMenu
                staticFormattingToolbar
                uploadFile={uploadFile}
                initialContent={validateInitialContent(bidResponse.response)}
                onChange={handleContentChange}
              />
            </MainContent>
            <Flex>
              {isSidebarOpen && <Resizer onMouseDown={onMouseDown} />}
              <Sidebar open={isSidebarOpen} width={sidebarWidth}>
                {isSidebarOpen && (
                  <Tabs
                    items={tabItems}
                    defaultValue={hasBidAccess ? TabKeys.AiAssistant : TabKeys.Details}
                    fullWidth
                    onChange={
                      hasBidAccess
                        ? undefined
                        : (key) => {
                            if (key === TabKeys.AiAssistant) {
                              openPaywallModal("BID_STOTLES_AI");
                            }
                          }
                    }
                  />
                )}
              </Sidebar>
            </Flex>
          </Flex>
        </Content>
      </Flex>

      {/* Export modal */}
      <ExportModal
        isOpen={exportModal.open}
        onClose={() => setExportModal({ ...exportModal, open: false })}
        onExport={handleExport}
        exportType={exportModal.type}
        filename={bidResponse.title || "Untitled response"}
      />

      <CustomerDocumentDrawer
        source={selectedSource}
        onClose={() => setSelectedSource(undefined)}
        isOpen={!!selectedSource}
      />
    </Container>
  );
}

const Resizer = styled.div(({ theme }) => ({
  width: 8,
  flexShrink: 0,
  cursor: "col-resize",
  backgroundColor: "transparent",
  "&:hover": { backgroundColor: theme.colors.sysBorderDecorative },
}));

const Container = styled(Flex)({
  flexDirection: "column",
  flexGrow: 1,
  height: "100vh",
  overflow: "hidden",
  position: "relative",
});

const Content = styled(Flex)({
  flexDirection: "column",
  flexGrow: 1,
  width: "100%",
  overflow: "hidden",
});

const MainContent = styled(Flex)<{ sidebarWidth: number }>(({ sidebarWidth: width }) => ({
  flexDirection: "column",
  gap: 24,
  width: `calc(100% - ${width}px)`,
  maxWidth: 960,
  margin: "0 auto",
  height: "100%",
  padding: 24,
  overflow: "auto",
  transition: smoothTransition,
}));

const Sidebar = styled(Flex)<{ open: boolean; width: number }>(({ theme, open, width }) => ({
  width: open ? width : 0,
  flexShrink: 0,
  borderLeft: open ? `1px solid ${theme.colors.sysBorderDecorative}` : undefined,
  height: "100%",
  flexDirection: "column",
  overflow: "hidden",
  transition: smoothTransition,
}));
